import React, { useState, useEffect } from "react";

import classNames from "classnames";
import * as fromProjects from "store/Projects";

import { a } from "@react-spring/web";
import { useIntersect } from "hooks/useIntersect";
import {
  useAnimateInTrail,
  INotify,
  fillNotifyArray,
} from "hooks/useAnimateInTrail";
import {
  initialOffset,
  draggableTileStaggerGap,
  animationTriggerThreshold,
} from "components/Animations/SpringProperties/SpringProperties";

import { useDraggableSlider } from "hooks/useDraggableSlider";
import { DragHand } from "components/DraggableGrids/DragHand/DragHand";

import { Image } from "./Image";

interface IProps {
  animationReady: boolean;
  bodyContent: object;
  sliderBackgroundShade: fromProjects.IBackgroundShadeTypes;
}

export const ImageSlider: React.FC<IProps> = ({
  animationReady,
  sliderBackgroundShade,
  ...props
}) => {
  const bodyContent: fromProjects.IContentImageSliderSection =
    props.bodyContent as fromProjects.IContentImageSliderSection;

  const [inViewRef, inView] = useIntersect({
    startObserving: animationReady,
  });

  const { content } = bodyContent;

  // const [notifyArray, setNotifyArray] = useState<INotify[]>([]);

  let notifyArray: INotify[] = [];

  content.rows.map((row, rowIndex) => {
    notifyArray = [
      ...notifyArray,
      ...fillNotifyArray(
        rowIndex + row.images.length,
        0,
        draggableTileStaggerGap,
        animationTriggerThreshold
      ),
      {
        index: draggableTileStaggerGap,
        threshold: animationTriggerThreshold,
      },
    ];
  });

  const notifyArrayLength =
    Math.max.apply(
      Math,
      notifyArray.map((o) => o.index)
    ) + 1;

  const [animateInTrail, triggerAnimateIn] = useAnimateInTrail(
    inView,
    notifyArrayLength,
    {
      notify: [...notifyArray],
    }
  );

  const gridWrapperStyles = {
    opacity: animateInTrail[0].active,
    transform: animateInTrail[0].active.to(
      (x) => `translateY(calc(${initialOffset(x)}))`
    ),
  };

  const commonGridBackgroundStyles = {
    backgroundColor: bodyContent.backgroundColor,
  };

  const gridBackgroundStyles = {
    ...commonGridBackgroundStyles,
    transform: animateInTrail[0].active.to(
      (x) => `translateX(calc((${x} * 150px) - 150px))`
    ),
  };

  const scale = 3;
  const [
    backgroundRef,
    contentRef,
    range,
    bind,
    isBeingDragged,
    xPosition,
    xVelocity,
  ] = useDraggableSlider(scale);

  const draggableWrapperStyles = {
    transform: xPosition.to(
      (x) => `translateX(${((x * scale) / range) * range}px)`
    ),
    willChange: isBeingDragged ? `transform` : ``,
  };

  return (
    <a.div
      ref={inViewRef}
      className={classNames("imageSliderWrapper", {
        multipleLines: content.rows.length > 1,
      })}
      style={gridWrapperStyles}
    >
      <a.div
        className="grid-background left"
        style={commonGridBackgroundStyles}
      />
      <a.div className="grid-background middle" style={gridBackgroundStyles} />
      <div className="grid-background contentHidden" ref={backgroundRef} />
      <div className="draggable-wrapper" {...bind()}>
        <div className="draggable-content">
          <a.div
            className={classNames("content-wrapper", {
              dragging: isBeingDragged,
            })}
            style={draggableWrapperStyles}
            ref={contentRef}
          >
            {content.rows.map((row, rowIndex) => (
              <div key={rowIndex} className="rowWrapper">
                {row.images.map((item, itemIndex) => (
                  <Image
                    animateIn={triggerAnimateIn(
                      (rowIndex + itemIndex) * draggableTileStaggerGap
                    )}
                    item={item}
                    velocity={xVelocity}
                    isBeingDragged={isBeingDragged}
                    key={itemIndex}
                  />
                ))}
              </div>
            ))}
            <DragHand
              animateIn={triggerAnimateIn(draggableTileStaggerGap)}
              visible={xPosition.to((x) => ((x * scale) / range) * range > -50)}
              backgroundShade={sliderBackgroundShade}
            />
          </a.div>
        </div>
      </div>
    </a.div>
  );
};
